import { Link } from "react-router-dom";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { nextgenAdminEvalCasesPath } from "~/pages/manifold-nextgen/paths";
import { EVAL_SCORE_MAX } from "~/services/evals";
import { ScoreCell } from "./CaseHealthMatrix";
import { rowActivateProps } from "./EvalTable";
import { CASES_PAGE_RUNS_PER_CASE } from "./constants/page";
import type { BrokenOrFlakyCase } from "./selectors/cases";

/** The cases page's strip length, so a case reads the same in both places. */
const STRIP_RUN_COUNT = CASES_PAGE_RUNS_PER_CASE;

/** The last column is ten 24px cells and the gaps between them, fixed so the header and every row share one grid. */
const ROW_GRID_CLS =
  "grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_276px] items-center gap-4 px-6 py-3";

const HEADER_CLS = "text-xs font-medium text-muted-foreground";

const ColumnHeaders = () => (
  <div className={cn(ROW_GRID_CLS, "py-2", HEADER_CLS)}>
    <span>Case</span>
    <span>Text</span>
    <span>Passed</span>
    <span className="text-right">Latest runs</span>
  </div>
);

const CaseRow = ({
  entry,
  onSelectCase,
}: {
  entry: BrokenOrFlakyCase;
  onSelectCase: (caseId: string, runId?: string) => void;
}) => {
  const { row, kind, textScore } = entry;
  return (
    <div
      {...rowActivateProps(
        () => onSelectCase(row.evalCase.id),
        cn(ROW_GRID_CLS, "border-t border-border transition-colors hover:bg-muted/40")
      )}
    >
      <button
        type="button"
        onClick={() => onSelectCase(row.evalCase.id)}
        className="truncate text-left font-mono text-[13px] font-semibold"
        title={row.evalCase.name}
      >
        {row.evalCase.name}
      </button>
      <span className="text-sm tabular-nums">
        {textScore == null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <>
            <span className={kind === "broken" ? "text-red-700" : "text-orange-700"}>
              {textScore.toFixed(2)}
            </span>
            <span className="text-muted-foreground"> / {EVAL_SCORE_MAX}</span>
          </>
        )}
      </span>
      <span
        className="text-sm tabular-nums text-muted-foreground"
        title={`${row.passedCount} of ${row.judgedCount} iterations passed`}
      >
        {row.passRate == null ? "—" : `${Math.round(row.passRate * 100)}%`}
      </span>
      <span className="flex justify-end gap-1">
        {row.cells.slice(-STRIP_RUN_COUNT).map((cell) => (
          <ScoreCell
            key={cell.runId}
            cell={cell}
            caseName={row.evalCase.name}
            onSelect={() => onSelectCase(row.evalCase.id, cell.runId)}
          />
        ))}
      </span>
    </div>
  );
};

/** The few cases most worth opening: worst pass rate first. */
export const BrokenFlakyCasesCard = ({
  cases,
  isLoading,
  onSelectCase,
}: {
  cases: BrokenOrFlakyCase[];
  isLoading: boolean;
  onSelectCase: (caseId: string, runId?: string) => void;
}) => (
  <Card role="region" aria-label="Broken & flaky cases" className="gap-0 py-0">
    <CardHeader className="flex flex-row items-center justify-between gap-4 px-6 pt-5 pb-3">
      <div>
        <CardTitle className="text-base font-semibold">Broken &amp; flaky cases</CardTitle>
        <p className="text-sm text-muted-foreground">
          Lowest pass rate across recent runs, worst first.
        </p>
      </div>
      <Button asChild variant="ghost" size="sm">
        <Link to={nextgenAdminEvalCasesPath()}>View all cases →</Link>
      </Button>
    </CardHeader>
    <CardContent className="p-0">
      {isLoading ? (
        // A finding, so it waits: an empty list read early says the suite is clean.
        <div role="status" aria-label="Loading cases">
          {[0, 1, 2].map((index) => (
            <div key={index} className={cn(ROW_GRID_CLS, "border-t border-border")}>
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-14" />
              <Skeleton className="h-4 w-8" />
              <Skeleton className="ml-auto h-5 w-32" />
            </div>
          ))}
        </div>
      ) : cases.length === 0 ? (
        <p className="border-t border-border px-6 py-8 text-center text-sm text-muted-foreground">
          No broken or flaky cases in recent runs.
        </p>
      ) : (
        <>
          <ColumnHeaders />
          {cases.map((entry) => (
            <CaseRow key={entry.row.evalCase.id} entry={entry} onSelectCase={onSelectCase} />
          ))}
        </>
      )}
    </CardContent>
  </Card>
);
